import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, Table2Icon, CompassIcon, ZapIcon, CheckCircle2Icon, SparklesIcon, UploadCloudIcon, AlertCircleIcon, SearchIcon, ArrowRightIcon, ShieldCheckIcon, MoreHorizontalIcon, XIcon } from 'lucide-react';
const FeatureCard = ({
  title,
  description,
  visual,
  isRight
}: {
  title: string;
  description: string;
  visual: React.ReactNode;
  isRight?: boolean;
}) => {
  return <motion.div initial={{
    opacity: 0,
    y: 40
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.6
  }} className={`flex flex-col ${isRight ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-24 items-center py-24`} data-id="element-81">
      <div className="flex-1 w-full" data-id="element-82">
        <div className="bg-gray-50 rounded-2xl border border-gray-100 p-8 shadow-sm aspect-[4/3] flex items-center justify-center overflow-hidden relative" data-id="element-83">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-[0.5]" data-id="element-84"></div>
          <div className="relative z-10 w-full flex justify-center" data-id="element-85">
            {visual}
          </div>
        </div>
      </div>
      <div className="flex-1 w-full" data-id="element-86">
        <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-87">
          {title}
        </h3>
        <p className="text-xl text-gray-600 leading-relaxed" data-id="element-88">{description}</p>
      </div>
    </motion.div>;
};
// --- Mock Components ---
const FormMock = () => <div className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden" data-id="element-89">
    <div className="bg-gray-50 px-4 py-3 border-b border-gray-100 flex justify-between items-center" data-id="element-90">
      <span className="text-sm font-semibold text-gray-700" data-id="element-91">
        New Vendor Onboarding
      </span>
      <div className="flex gap-1.5" data-id="element-92">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-93"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" data-id="element-94"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" data-id="element-95"></div>
      </div>
    </div>
    <div className="p-5 space-y-4" data-id="element-96">
      {/* File Upload Area */}
      <div className="border-2 border-dashed border-blue-200 bg-blue-50 rounded-lg p-3 flex items-center gap-3" data-id="element-97">
        <div className="bg-white p-2 rounded-md shadow-sm text-blue-600" data-id="element-98">
          <FileTextIcon size={18} data-id="element-99" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-100">
          <div className="text-xs font-medium text-blue-900 truncate" data-id="element-101">
            acme_w9_signed.pdf
          </div>
          <div className="text-[10px] text-blue-600 flex items-center gap-1" data-id="element-102">
            <SparklesIcon size={10} data-id="element-103" />3 fields extracted
          </div>
        </div>
        <CheckCircle2Icon size={16} className="text-blue-500" data-id="element-104" />
      </div>

      {/* Fields */}
      <div className="space-y-3" data-id="element-105">
        <div data-id="element-106">
          <label className="text-xs font-medium text-gray-500 mb-1 block" data-id="element-107">
            Company Name
          </label>
          <div className="relative" data-id="element-108">
            <input type="text" value="Acme Corp Inc." readOnly className="w-full text-sm border-blue-300 bg-blue-50/50 rounded-md px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20" data-id="element-109" />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded font-medium" data-id="element-110">
              <SparklesIcon size={10} data-id="element-111" />
              AI
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3" data-id="element-112">
          <div data-id="element-113">
            <label className="text-xs font-medium text-gray-500 mb-1 block" data-id="element-114">
              Tax ID
            </label>
            <div className="relative" data-id="element-115">
              <input type="text" value="XX-XXXXXXX" readOnly className="w-full text-sm border-gray-200 rounded-md px-3 py-2 text-gray-800" data-id="element-116" />
              <CheckCircle2Icon size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500" data-id="element-117" />
            </div>
          </div>
          <div data-id="element-118">
            <label className="text-xs font-medium text-gray-500 mb-1 block" data-id="element-119">
              Industry
            </label>
            <input type="text" value="Manufacturing" readOnly className="w-full text-sm border-gray-200 rounded-md px-3 py-2 text-gray-800" data-id="element-120" />
          </div>
        </div>
      </div>

      <button className="w-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium py-2.5 rounded-lg transition-colors shadow-sm mt-2" data-id="element-121">
        Create Vendor
      </button>
    </div>
  </div>;
const TableMock = () => <div className="w-full max-w-lg bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col" data-id="element-122">
    {/* Header */}
    <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 flex justify-between items-center" data-id="element-123">
      <div className="flex items-center gap-2.5" data-id="element-124">
        <div className="bg-orange-100 p-1.5 rounded-md text-orange-600" data-id="element-125">
          <SparklesIcon size={16} data-id="element-126" />
        </div>
        <span className="text-sm font-medium text-gray-500" data-id="element-127">
          Dataset:{' '}
          <span className="text-gray-900 font-semibold" data-id="element-128">Sales Q4 2024</span>
        </span>
      </div>
      <div className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-semibold flex items-center gap-1.5" data-id="element-129">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-130"></span>
        Live
      </div>
    </div>

    {/* Query Input */}
    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3" data-id="element-131">
      <SearchIcon size={18} className="text-gray-400 flex-shrink-0" data-id="element-132" />
      <div className="flex-1 text-base font-medium text-gray-900" data-id="element-133">
        Show Q4 revenue by region
      </div>
      <div className="flex items-center gap-1.5 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-md font-semibold flex-shrink-0" data-id="element-134">
        <SparklesIcon size={12} data-id="element-135" />
        AI
      </div>
    </div>

    {/* Viz Toggle */}
    <div className="px-5 pt-4 flex gap-1.5" data-id="element-136">
      {['Bar', 'Line', 'Table', 'Pie'].map(type => <button key={type} className={`text-xs font-semibold px-3.5 py-1.5 rounded-lg transition-colors ${type === 'Bar' ? 'bg-orange-500 text-white shadow-sm' : 'bg-gray-100 text-gray-500'}`} data-id="element-137">
          {type}
        </button>)}
    </div>

    {/* Bar Chart */}
    <div className="px-5 pt-5 pb-4" data-id="element-138">
      <div className="flex items-end gap-3 h-36" data-id="element-139">
        {[{
        label: 'West',
        value: 82,
        amount: '$4.1M',
        color: 'from-orange-500 to-orange-400'
      }, {
        label: 'East',
        value: 65,
        amount: '$3.2M',
        color: 'from-orange-400 to-amber-400'
      }, {
        label: 'Central',
        value: 48,
        amount: '$2.4M',
        color: 'from-amber-400 to-yellow-400'
      }, {
        label: 'South',
        value: 100,
        amount: '$5.0M',
        color: 'from-orange-600 to-orange-500'
      }, {
        label: 'APAC',
        value: 38,
        amount: '$1.9M',
        color: 'from-amber-300 to-yellow-300'
      }].map(bar => <div key={bar.label} className="flex-1 flex flex-col items-center gap-1.5" style={{
        height: '100%'
      }} data-id="element-140">
            <span className="text-xs font-bold text-gray-700" data-id="element-141">
              {bar.amount}
            </span>
            <div className="flex-1 w-full flex items-end" data-id="element-142">
              <div className={`w-full rounded-t-lg bg-gradient-to-t ${bar.color} transition-all relative`} style={{
            height: `${bar.value}%`
          }} data-id="element-143">
                {bar.label === 'South' && <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap shadow-md" data-id="element-144">
                    Highest ↑
                  </div>}
              </div>
            </div>
            <span className="text-xs text-gray-500 font-semibold" data-id="element-145">
              {bar.label}
            </span>
          </div>)}
      </div>
    </div>

    {/* AI Insight Footer */}
    <div className="bg-blue-50 px-5 py-3 border-t border-blue-100 flex items-start gap-2.5" data-id="element-146">
      <SparklesIcon size={14} className="text-blue-500 mt-0.5 flex-shrink-0" data-id="element-147" />
      <span className="text-xs text-blue-800 font-medium leading-relaxed" data-id="element-148">
        South region leads at $5.0M (+23% QoQ). APAC underperforming vs. target.
      </span>
    </div>
  </div>;
const NavMock = () => <div className="w-full max-w-sm" data-id="element-149">
    <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden" data-id="element-150">
      {/* Search Input */}
      <div className="p-4 border-b border-gray-100 flex items-center gap-3" data-id="element-151">
        <SearchIcon size={18} className="text-gray-400" data-id="element-152" />
        <div className="flex-1 text-sm text-gray-900" data-id="element-153">
          vendor settings for Acme
          <span className="animate-pulse inline-block w-0.5 h-4 bg-orange-500 align-middle ml-0.5" data-id="element-154"></span>
        </div>
        <div className="text-[10px] font-mono text-gray-400 border border-gray-200 rounded px-1.5 py-0.5" data-id="element-155">
          ESC
        </div>
      </div>

      {/* Results */}
      <div className="p-2 bg-gray-50/50" data-id="element-156">
        <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2" data-id="element-157">
          AI Suggested Route
        </div>
        <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 mx-1 mb-1 cursor-pointer hover:bg-orange-100 transition-colors group" data-id="element-158">
          <div className="flex items-center gap-3 mb-2" data-id="element-159">
            <div className="bg-white p-1.5 rounded-md shadow-sm text-orange-600" data-id="element-160">
              <CompassIcon size={16} data-id="element-161" />
            </div>
            <div className="flex-1" data-id="element-162">
              <div className="text-sm font-medium text-gray-900 group-hover:text-orange-700" data-id="element-163">
                Go to Acme Corp Settings
              </div>
              <div className="text-xs text-gray-500" data-id="element-164">
                Direct navigation to vendor details
              </div>
            </div>
            <ArrowRightIcon size={16} className="text-orange-400" data-id="element-165" />
          </div>
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-medium pl-1" data-id="element-166">
            <span data-id="element-167">Settings</span>
            <span className="text-gray-300" data-id="element-168">/</span>
            <span data-id="element-169">Vendors</span>
            <span className="text-gray-300" data-id="element-170">/</span>
            <span className="text-orange-600 bg-orange-100 px-1 rounded" data-id="element-171">
              Acme Corp
            </span>
          </div>
        </div>

        <div className="px-3 py-2 text-xs text-gray-500 hover:bg-gray-100 rounded-lg cursor-pointer mx-1 flex items-center gap-2" data-id="element-172">
          <SearchIcon size={14} data-id="element-173" />
          Search all vendors for "Acme"
        </div>
      </div>
    </div>
  </div>;
const ActionMock = () => <div className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden" data-id="element-174">
    <div className="bg-gray-900 text-white px-5 py-4 flex justify-between items-start" data-id="element-175">
      <div className="flex gap-3" data-id="element-176">
        <div className="bg-gray-800 p-2 rounded-lg text-orange-400" data-id="element-177">
          <ZapIcon size={20} data-id="element-178" />
        </div>
        <div data-id="element-179">
          <h4 className="font-semibold text-sm" data-id="element-180">Create Vendor</h4>
          <p className="text-xs text-gray-400 mt-0.5" data-id="element-181">
            Tool: <span className="font-mono" data-id="element-182">create_vendor_api</span>
          </p>
        </div>
      </div>
      <div className="bg-gray-800 text-[10px] font-mono py-1 px-2 rounded text-gray-300" data-id="element-183">
        PENDING
      </div>
    </div>

    <div className="p-5" data-id="element-184">
      {/* Parameters */}
      <div className="space-y-2 mb-5" data-id="element-185">
        <div className="flex justify-between text-sm border-b border-gray-50 pb-2" data-id="element-186">
          <span className="text-gray-500" data-id="element-187">Name</span>
          <span className="font-medium text-gray-900" data-id="element-188">Acme Corp</span>
        </div>
        <div className="flex justify-between text-sm border-b border-gray-50 pb-2" data-id="element-189">
          <span className="text-gray-500" data-id="element-190">Type</span>
          <span className="font-medium text-gray-900" data-id="element-191">Supplier</span>
        </div>
        <div className="flex justify-between text-sm pb-1" data-id="element-192">
          <span className="text-gray-500" data-id="element-193">Region</span>
          <span className="font-medium text-gray-900" data-id="element-194">US-West</span>
        </div>
      </div>

      {/* Checks */}
      <div className="bg-gray-50 rounded-lg p-3 mb-5 space-y-2" data-id="element-195">
        <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-196">
          <ShieldCheckIcon size={14} className="text-green-500" data-id="element-197" />
          <span data-id="element-198">Auth scope verified</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-199">
          <CheckCircle2Icon size={14} className="text-green-500" data-id="element-200" />
          <span data-id="element-201">Budget check passed</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-202">
          <CheckCircle2Icon size={14} className="text-green-500" data-id="element-203" />
          <span data-id="element-204">Rate limit OK</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3" data-id="element-205">
        <button className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2 rounded-lg transition-colors" data-id="element-206">
          Reject
        </button>
        <button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium py-2 rounded-lg transition-colors shadow-sm" data-id="element-207">
          Approve
        </button>
      </div>
    </div>

    {/* Audit Footer */}
    <div className="bg-gray-50 px-5 py-2 border-t border-gray-100 text-[10px] text-gray-400 font-mono flex justify-between" data-id="element-208">
      <span data-id="element-209">Action logged</span>
      <span data-id="element-210">Trace ID: abc-123</span>
    </div>
  </div>;
const ComponentsZigzag = () => {
  return <section className="w-full bg-gradient-to-b from-white via-orange-50/20 to-white py-16 sm:py-24 md:py-32 lg:py-48" data-id="element-211">
      <div className="container mx-auto px-4" data-id="element-212">
        <div className="max-w-4xl mx-auto text-center mb-24" data-id="element-213">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight" data-id="element-214">
            Drop-in agentic surfaces
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-215">
            Start with core components — each designed for a different class of
            in-product work. Ship fast with ours, then extend with your own
            surfaces on the same runtime.
          </p>
        </div>

        <div className="max-w-6xl mx-auto" data-id="element-216">
          <FeatureCard title="Agentic Forms" description="Multimodal intake → extraction → validation → completion. Users can type, speak, paste, or upload — FluidWeb extracts structured data, validates against your schema, and fills your form with intelligent follow-ups." visual={<FormMock data-id="element-218" />} data-id="element-217" />
          <FeatureCard title="Agentic Data Explorer" description="Replace static dashboards with bounded AI exploration. Users query a defined dataset in natural language and instantly switch between charts, tables, and summaries — all constrained to your data boundaries." visual={<DataExplorerMock data-id="element-220" />} isRight={true} data-id="element-219" />
          <FeatureCard title="Agentic Navigation" description="Goal-based routing inside complex apps. Users describe what they want, AI routes them to the correct screen, pre-fills context, and guides execution — without unsafe free roaming." visual={<NavMock data-id="element-222" />} data-id="element-221" />
          <FeatureCard title="Agentic Actions" description="Tool-calling with built-in controls. Every action is parameter-validated, permission-checked, policy-gated, and logged — with approvals when required and replay when needed." visual={<ActionMock data-id="element-224" />} isRight={true} data-id="element-223" />
        </div>
      </div>
    </section>;
};
export default ComponentsZigzag;
const DATA = [{
  label: 'West',
  value: 4.1,
  pct: 82
}, {
  label: 'East',
  value: 3.2,
  pct: 65
}, {
  label: 'Central',
  value: 2.4,
  pct: 48
}, {
  label: 'South',
  value: 5.0,
  pct: 100
}, {
  label: 'APAC',
  value: 1.9,
  pct: 38
}];
const BarView = () => <div className="px-5 pt-5 pb-4" data-id="element-225">
    <div className="flex items-end gap-3" style={{
    height: 144
  }} data-id="element-226">
      {DATA.map(bar => <div key={bar.label} className="flex-1 flex flex-col items-center gap-1.5" style={{
      height: '100%'
    }} data-id="element-227">
          <span className="text-xs font-bold text-gray-700" data-id="element-228">${bar.value}M</span>
          <div className="flex-1 w-full flex items-end" data-id="element-229">
            <div className="w-full rounded-t-lg relative" style={{
          height: `${bar.pct}%`,
          background: bar.pct === 100 ? 'linear-gradient(to top, #ea580c, #f97316)' : bar.pct > 60 ? 'linear-gradient(to top, #f97316, #fb923c)' : 'linear-gradient(to top, #fb923c, #fbbf24)'
        }} data-id="element-230">
              {bar.label === 'South' && <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap shadow-md" data-id="element-231">
                  Highest ↑
                </div>}
            </div>
          </div>
          <span className="text-xs text-gray-500 font-semibold" data-id="element-232">
            {bar.label}
          </span>
        </div>)}
    </div>
  </div>;
const LineView = () => {
  const maxVal = 5.0;
  const points = DATA.map((d, i) => ({
    x: 20 + i * ((100 - 40) / (DATA.length - 1)),
    y: 90 - d.value / maxVal * 70,
    ...d
  }));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = `${pathD} L ${points[points.length - 1].x} 90 L ${points[0].x} 90 Z`;
  return <div className="px-5 pt-5 pb-4" data-id="element-233">
      <svg viewBox="0 0 100 100" className="w-full" style={{
      height: 144
    }} data-id="element-234">
        <defs data-id="element-235">
          <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-236">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.3" data-id="element-237" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0" data-id="element-238" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[20, 40, 60, 80].map(y => <line key={y} x1="15" y1={y} x2="85" y2={y} stroke="#e5e7eb" strokeWidth="0.3" data-id="element-239" />)}
        {/* Area fill */}
        <path d={areaD} fill="url(#lineGrad)" data-id="element-240" />
        {/* Line */}
        <path d={pathD} fill="none" stroke="#f97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-241" />
        {/* Dots and labels */}
        {points.map(p => <g key={p.label} data-id="element-242">
            <circle cx={p.x} cy={p.y} r="2" fill="#fff" stroke="#f97316" strokeWidth="1.2" data-id="element-243" />
            <text x={p.x} y={p.y - 5} textAnchor="middle" fontSize="4" fontWeight="700" fill="#374151" data-id="element-244">
              ${p.value}M
            </text>
            <text x={p.x} y="97" textAnchor="middle" fontSize="3.5" fontWeight="600" fill="#6b7280" data-id="element-245">
              {p.label}
            </text>
          </g>)}
      </svg>
    </div>;
};
const TableView = () => <div className="px-5 pt-4 pb-4" data-id="element-246">
    <table className="w-full text-sm" data-id="element-247">
      <thead data-id="element-248">
        <tr className="border-b border-gray-200" data-id="element-249">
          <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-250">
            Region
          </th>
          <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-251">
            Revenue
          </th>
          <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-252">
            Share
          </th>
        </tr>
      </thead>
      <tbody data-id="element-253">
        {DATA.map(d => <tr key={d.label} className={`border-b border-gray-50 ${d.label === 'South' ? 'bg-orange-50/50' : ''}`} data-id="element-254">
            <td className="py-2.5 font-medium text-gray-900" data-id="element-255">{d.label}</td>
            <td className="py-2.5 text-right text-gray-700 font-medium" data-id="element-256">
              ${d.value}M
            </td>
            <td className="py-2.5 text-right" data-id="element-257">
              <div className="flex items-center justify-end gap-2" data-id="element-258">
                <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-259">
                  <div className="h-full rounded-full" style={{
                width: `${d.pct}%`,
                background: '#f97316'
              }} data-id="element-260"></div>
                </div>
                <span className="text-xs text-gray-500 w-8 text-right" data-id="element-261">
                  {Math.round(d.value / 16.6 * 100)}%
                </span>
              </div>
            </td>
          </tr>)}
      </tbody>
    </table>
  </div>;
const PieView = () => {
  const total = DATA.reduce((s, d) => s + d.value, 0);
  const colors = ['#ea580c', '#f97316', '#fb923c', '#fdba74', '#fde68a'];
  let cumAngle = 0;
  return <div className="px-5 pt-4 pb-4 flex items-center gap-4" data-id="element-262">
      <svg viewBox="0 0 100 100" className="w-28 h-28 flex-shrink-0" data-id="element-263">
        {DATA.map((d, i) => {
        const angle = d.value / total * 360;
        const startAngle = cumAngle;
        cumAngle += angle;
        const startRad = (startAngle - 90) * Math.PI / 180;
        const endRad = (startAngle + angle - 90) * Math.PI / 180;
        const largeArc = angle > 180 ? 1 : 0;
        const x1 = 50 + 40 * Math.cos(startRad);
        const y1 = 50 + 40 * Math.sin(startRad);
        const x2 = 50 + 40 * Math.cos(endRad);
        const y2 = 50 + 40 * Math.sin(endRad);
        return <path key={d.label} d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`} fill={colors[i]} stroke="#fff" strokeWidth="1" data-id="element-264" />;
      })}
        <circle cx="50" cy="50" r="20" fill="#fff" data-id="element-265" />
        <text x="50" y="48" textAnchor="middle" fontSize="6" fontWeight="800" fill="#111827" data-id="element-266">
          ${total.toFixed(1)}M
        </text>
        <text x="50" y="56" textAnchor="middle" fontSize="3.5" fill="#6b7280" data-id="element-267">
          Total
        </text>
      </svg>
      <div className="flex-1 space-y-1.5" data-id="element-268">
        {DATA.map((d, i) => <div key={d.label} className="flex items-center gap-2 text-xs" data-id="element-269">
            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{
          background: colors[i]
        }} data-id="element-270"></div>
            <span className="text-gray-700 font-medium flex-1" data-id="element-271">{d.label}</span>
            <span className="text-gray-500 font-semibold" data-id="element-272">${d.value}M</span>
          </div>)}
      </div>
    </div>;
};
const DataExplorerMock = () => {
  const [activeViz, setActiveViz] = useState('Bar');
  return <div className="w-full max-w-lg bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col" data-id="element-273">
      {/* Header */}
      <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 flex justify-between items-center" data-id="element-274">
        <div className="flex items-center gap-2.5" data-id="element-275">
          <div className="bg-orange-100 p-1.5 rounded-md text-orange-600" data-id="element-276">
            <SparklesIcon size={16} data-id="element-277" />
          </div>
          <span className="text-sm font-medium text-gray-500" data-id="element-278">
            Dataset:{' '}
            <span className="text-gray-900 font-semibold" data-id="element-279">Sales Q4 2024</span>
          </span>
        </div>
        <div className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-semibold flex items-center gap-1.5" data-id="element-280">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-281"></span>
          Live
        </div>
      </div>

      {/* Query Input */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3" data-id="element-282">
        <SearchIcon size={18} className="text-gray-400 flex-shrink-0" data-id="element-283" />
        <div className="flex-1 text-base font-medium text-gray-900" data-id="element-284">
          Show Q4 revenue by region
        </div>
        <div className="flex items-center gap-1.5 bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-md font-semibold flex-shrink-0" data-id="element-285">
          <SparklesIcon size={12} data-id="element-286" />
          AI
        </div>
      </div>

      {/* Viz Toggle */}
      <div className="px-5 pt-4 flex gap-1.5" data-id="element-287">
        {['Bar', 'Line', 'Table', 'Pie'].map(type => <button key={type} onClick={() => setActiveViz(type)} className={`text-xs font-semibold px-3.5 py-1.5 rounded-lg transition-colors ${activeViz === type ? 'bg-orange-500 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} data-id="element-288">
            {type}
          </button>)}
      </div>

      {/* Active Visualization */}
      {activeViz === 'Bar' && <BarView data-id="element-289" />}
      {activeViz === 'Line' && <LineView data-id="element-290" />}
      {activeViz === 'Table' && <TableView data-id="element-291" />}
      {activeViz === 'Pie' && <PieView data-id="element-292" />}

      {/* AI Insight Footer */}
      <div className="bg-blue-50 px-5 py-3 border-t border-blue-100 flex items-start gap-2.5" data-id="element-293">
        <SparklesIcon size={14} className="text-blue-500 mt-0.5 flex-shrink-0" data-id="element-294" />
        <span className="text-xs text-blue-800 font-medium leading-relaxed" data-id="element-295">
          South region leads at $5.0M (+23% QoQ). APAC underperforming vs.
          target.
        </span>
      </div>
    </div>;
};